// src/components/MappingEditor.tsx

import React, { useEffect, useState } from "react";
import {
  PdfMappingDefinition,
  DiscoveredPdfField,
  CheckBoxCondition,
  TextFieldDefault,
  CheckBoxMapping,
  ConditionalTextField,
  AggregatedTextField,
  ConditionalValueSuffix,
  MappingCondition,
  ObjectMapping,
  AuthorInfoFields,
  LookupTextField,
} from "../../../../interfaces/dynamicForms/MappingTypes";
import { FormDefinition } from "../../../../interfaces/dynamicForms/FormBuilder";
import { useAuth } from "../../../../firebase/AuthProvider";
import { getTaskTypes } from "../../../../services/Lookups/TaskLookupService";
import { getWaiverTypes } from "../../../../services/Lookups/WaiverLookupService";

// --- UI Constants & Helper Types ---

const MAPPING_TYPE_OPTIONS = [
  "Direct Text Field",
  "Object Property Text Field",
  "Author Information",
  "Conditional Checkbox",
  "Paired Yes/No Checkbox",
  "Text Field with Default",
  "Aggregated Text Field",
  "Conditional Text Field",
  "Lookup Value to Text Field",
] as const;
type UIMappingType = (typeof MAPPING_TYPE_OPTIONS)[number];

interface FieldMappingInfo {
  isMapped: boolean;
  type: UIMappingType | null;
  details?: any;
}

// --- State Management Logic ---

/**
 * Creates an immutable copy of the mapping object with the specified field's mapping removed.
 */
function removeMappingForPdfField(
  pdfFieldName: string,
  mapping: PdfMappingDefinition
): PdfMappingDefinition {
  const newMapping = { ...mapping };

  if (newMapping.AuthorInfoFields) {
    if (Array.isArray(newMapping.AuthorInfoFields)) {
      // NEW Format: Filter the array
      newMapping.AuthorInfoFields = newMapping.AuthorInfoFields.filter(
        (m) => m.PdfField !== pdfFieldName
      );
    } else {
      // OLD Format: It is an object.
      // We will MIGRATE it to an array now while removing the target field.
      const migratedArray: any[] = [];
      const oldObj = newMapping.AuthorInfoFields as any;

      Object.keys(oldObj).forEach((key) => {
        const val = oldObj[key];
        // If this isn't the field we are deleting, keep it and convert structure
        if (val !== pdfFieldName) {
          migratedArray.push({
            AuthorField: key,
            PdfField: val,
          });
        }
      });

      newMapping.AuthorInfoFields = migratedArray;
    }
  }

  // Remove from TextFieldMappings
  if (newMapping.TextFieldMappings) {
    const updatedTextFields: typeof newMapping.TextFieldMappings = {};
    for (const [key, value] of Object.entries(newMapping.TextFieldMappings)) {
      if (value !== pdfFieldName) {
        updatedTextFields[key] = value;
      }
    }
    newMapping.TextFieldMappings = updatedTextFields;
  }

  if (newMapping.CheckBoxConditions) {
    newMapping.CheckBoxConditions = newMapping.CheckBoxConditions.filter(
      (c) => c.Checkbox !== pdfFieldName
    );
  }

  if (newMapping.CheckBoxMappings) {
    const updatedCheckboxMaps: typeof newMapping.CheckBoxMappings = {};
    for (const [key, pair] of Object.entries(newMapping.CheckBoxMappings)) {
      if (pair.Yes !== pdfFieldName && pair.No !== pdfFieldName) {
        updatedCheckboxMaps[key] = pair;
      }
    }
    newMapping.CheckBoxMappings = updatedCheckboxMaps;
  }

  if (newMapping.TextFieldDefaults) {
    newMapping.TextFieldDefaults = newMapping.TextFieldDefaults.filter(
      (d) => d.PdfField !== pdfFieldName
    );
  }

  if (newMapping.AggregatedTextFields) {
    newMapping.AggregatedTextFields = newMapping.AggregatedTextFields.filter(
      (a) => a.PdfField !== pdfFieldName
    );
  }

  if (newMapping.ConditionalTextFields) {
    newMapping.ConditionalTextFields = newMapping.ConditionalTextFields.filter(
      (c) => c.ResultField !== pdfFieldName
    );
  }

  if (newMapping.ObjectMappings) {
    newMapping.ObjectMappings = newMapping.ObjectMappings.map(om => ({
      ...om,
      Fields: om.Fields.filter(f => f.PdfField !== pdfFieldName)
    })).filter(om => om.Fields.length > 0);
  }

  if ((newMapping as any).LookupTextFields) {
    (newMapping as any).LookupTextFields = (newMapping as any).LookupTextFields.filter(
      (l: any) => l.PdfField !== pdfFieldName
    );
  }

  return newMapping;
}

/**
 * The "brain" of the editor. Finds how a PDF field is currently used.
 */
function findMappingForPdfField(
  pdfFieldName: string,
  mapping: PdfMappingDefinition
): FieldMappingInfo {
  if (!mapping) return { isMapped: false, type: null };

  // Check AuthorInfoFields first
  if (mapping.AuthorInfoFields) {
    if (Array.isArray(mapping.AuthorInfoFields)) {
      // NEW Format: It is a List/Array
      const match = mapping.AuthorInfoFields.find(
        (m) => m.PdfField === pdfFieldName
      );
      if (match) {
        return {
          isMapped: true,
          type: "Author Information",
          details: { authorField: match.AuthorField },
        };
      }
    } else {
      // OLD Format: It is a Dictionary/Object { "SCID": "PdfField1" }
      // We search the values of the object
      const authorFieldKey = Object.keys(mapping.AuthorInfoFields).find(
        (key) => (mapping.AuthorInfoFields as any)[key] === pdfFieldName
      );
      if (authorFieldKey) {
        return {
          isMapped: true,
          type: "Author Information",
          details: { authorField: authorFieldKey },
        };
      }
    }
  }
  // The order of these checks determines priority if a field is somehow mapped multiple times.
  if (mapping.TextFieldMappings) {
    const key = Object.keys(mapping.TextFieldMappings).find(
      (k) =>
        typeof mapping.TextFieldMappings![k] === "string" &&
        mapping.TextFieldMappings![k] === pdfFieldName
    );
    if (key)
      return {
        isMapped: true,
        type: "Direct Text Field",
        details: { dataKey: key },
      };
  }

  if (mapping.ObjectMappings) {
    for (const objMapping of mapping.ObjectMappings) {
      const fieldMapping = objMapping.Fields.find(f => f.PdfField === pdfFieldName);
      if (fieldMapping) {
        return {
          isMapped: true,
          type: "Object Property Text Field",
          details: {
            objectName: objMapping.ObjectName,
            sourceField: fieldMapping.SourceField,
            combineWith: (fieldMapping as any).CombineWith,
            separator: (fieldMapping as any).Separator,
          }
        };
      }
    }
  }

  if (mapping.CheckBoxConditions) {
    const condition = mapping.CheckBoxConditions.find(
      (c) => c.Checkbox === pdfFieldName
    );
    if (condition)
      return {
        isMapped: true,
        type: "Conditional Checkbox",
        details: condition,
      };
  }

  if (mapping.CheckBoxMappings) {
    for (const [dataKey, pair] of Object.entries(mapping.CheckBoxMappings)) {
      if (pair.Yes === pdfFieldName)
        return {
          isMapped: true,
          type: "Paired Yes/No Checkbox",
          details: { dataKey, role: "Yes", pair },
        };
      if (pair.No === pdfFieldName)
        return {
          isMapped: true,
          type: "Paired Yes/No Checkbox",
          details: { dataKey, role: "No", pair },
        };
    }
  }

  if (mapping.TextFieldDefaults) {
    const defaultRule = mapping.TextFieldDefaults.find(
      (d) => d.PdfField === pdfFieldName
    );
    if (defaultRule)
      return {
        isMapped: true,
        type: "Text Field with Default",
        details: defaultRule,
      };
  }

  if (mapping.AggregatedTextFields) {
    const aggField = mapping.AggregatedTextFields.find(
      (a) => a.PdfField === pdfFieldName
    );
    if (aggField)
      return {
        isMapped: true,
        type: "Aggregated Text Field",
        details: aggField,
      };
  }

  if (mapping.ConditionalTextFields) {
    const condField = mapping.ConditionalTextFields.find(
      (c) => c.ResultField === pdfFieldName
    );
    if (condField)
      return {
        isMapped: true,
        type: "Conditional Text Field",
        details: condField,
      };
  }

  if ((mapping as any).LookupTextFields) {
    const lookupField = (mapping as any).LookupTextFields.find(
      (l: any) => l.PdfField === pdfFieldName
    );
    if (lookupField)
      return {
        isMapped: true,
        type: "Lookup Value to Text Field",
        details: lookupField,
      };
  }

  return { isMapped: false, type: null };
}

const SuffixConditionEditor: React.FC<{
  condition: Partial<MappingCondition>;
  onConditionChange: (newCondition: Partial<MappingCondition>) => void;
}> = ({ condition, onConditionChange }) => {
  // Handles the comma-separated list of values for the condition
  const handleValuesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const values = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
    onConditionChange({ Values: values });
  };

  return (
    <div className="p-2 border border-dashed border-gray-400 rounded bg-gray-100 space-y-2">
      <h5 className="text-xs font-bold text-gray-600">Suffix Condition</h5>
      <span className="italic text-gray-400 text-xs block">Set an optional condition that must be true for this suffix rule to apply.</span>
      <div>
        <label className="text-xs font-medium text-gray-500">Condition Source Prefix</label>
        <span className="italic text-gray-400 text-xs block">Enter the text prefix used to find the data field needed for this condition check.</span>
        <input type="text" value={condition.SourcePrefix || ''} onChange={e => onConditionChange({ SourcePrefix: e.target.value })} className="block w-full mt-1 px-2 py-1 border rounded-md text-xs" placeholder="e.g., monitor_" />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-500">Condition Values (comma-separated)</label>
        <span className="italic text-gray-400 text-xs block">Provide a comma-separated list of values that the condition field must match for the rule to activate.</span>
        <input type="text" defaultValue={(condition.Values || []).join(', ')} onChange={handleValuesChange} className="block w-full mt-1 px-2 py-1 border rounded-md text-xs" placeholder="e.g., 13, 23, 31" />
      </div>
    </div>
  );
};


const ValueSuffixEditor: React.FC<{
  suffix: Partial<ConditionalValueSuffix>;
  onSuffixChange: (newSuffix: Partial<ConditionalValueSuffix>) => void;
  onDelete: () => void;
}> = ({ suffix, onSuffixChange, onDelete }) => (
  <div className="p-3 border border-gray-300 rounded bg-white space-y-3 relative">
    <button onClick={onDelete} className="absolute top-2 right-2 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold hover:bg-red-200">X</button>
    <div>
      <label className="text-xs font-medium text-gray-600">Related Data Source Prefix</label>
      <span className="italic text-gray-400 text-xs block">Enter the text prefix used to find a related answer based on the value from the "Source Question".</span>
      <input type="text" value={suffix.SourcePrefix || ''} onChange={e => onSuffixChange({ ...suffix, SourcePrefix: e.target.value })} className="block w-full mt-1 px-2 py-1 border rounded-md text-sm" placeholder="e.g., remediation_" />
    </div>
    <label className="flex items-center space-x-2 cursor-pointer">
      <input type="checkbox" checked={suffix.CombineResult || false} onChange={(e) => onSuffixChange({ ...suffix, CombineResult: e.target.checked })} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
      <span className="text-sm text-gray-700">Combine Result</span>
    </label>
    <span className="italic text-gray-400 text-xs block"> Check this box to combine the source value and its related text into a single, final string for this item.</span>
    <SuffixConditionEditor condition={suffix.Condition || {}} onConditionChange={(c) => onSuffixChange({ ...suffix, Condition: c })} />
  </div>
);

// --- Conditional Sub-Forms ---

const QuestionIdSelect: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  allQuestionIds: string[];
}> = ({ label, value, onChange, allQuestionIds }) => (
  <div>
    <label className="text-xs font-medium text-gray-600">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
    >
      <option value="" disabled>
        -- Select Question --
      </option>
      {allQuestionIds.map((id) => (
        <option key={id} value={id}>
          {id}
        </option>
      ))}
    </select>
  </div>
);

const ConditionalCheckboxForm: React.FC<{
  details: Partial<CheckBoxCondition>;
  onDetailsChange: (newDetails: Partial<CheckBoxCondition>) => void;
  allQuestionIds: string[];
  formDefinition: FormDefinition;
}> = ({ details, onDetailsChange, allQuestionIds, formDefinition }) => {
  const { currentUser } = useAuth();
  const [lookupOptions, setLookupOptions] = useState<{ value: string; label: string }[]>([]);

  const selectedQuestion = details.Field
    ? formDefinition.steps
      .flatMap((step) => step.questions)
      .find((q) => q.id === details.Field)
    : null;

  const isDynamic = !!selectedQuestion?.dynamicSource;

  useEffect(() => {
    const fetchLookupOptions = async () => {
      if (!isDynamic || !selectedQuestion || !currentUser) {
        setLookupOptions([]);
        return;
      }

      const token = await currentUser.getIdToken();
      let options: { value: string; label: string }[] = [];

      if (selectedQuestion.dynamicSource === 'taskContactTypes') {
        const taskTypes = await getTaskTypes(token);
        if (taskTypes) {
          options = taskTypes.map(t => ({ value: t.type, label: t.type }));
        }
      } else if (selectedQuestion.dynamicSource === 'waiverTypes') {
        const waiverTypes = await getWaiverTypes(token);
        if (waiverTypes) {
          options = waiverTypes.map(w => ({ value: w.name, label: w.name }));
        }
      }

      setLookupOptions(options);
    };

    fetchLookupOptions();
  }, [selectedQuestion, currentUser, isDynamic]);


  const isSelectableType =
    selectedQuestion &&
    (selectedQuestion.type === "react-select" ||
      selectedQuestion.type === "radio");

  const currentValues = details.Values || (details.Value ? [details.Value] : []);

  const handleMultiSelectChange = (optionValue: string) => {
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter((v) => v !== optionValue)
      : [...currentValues, optionValue];
    onDetailsChange({ ...details, Values: newValues, Value: undefined });
  };

  const handleTextValuesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = e.target.value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    onDetailsChange({ ...details, Values: newValues, Value: undefined });
  };

  return (
    <div className="space-y-2">
      <QuestionIdSelect
        label="If Question ID..."
        value={details.Field || ""}
        onChange={(val) =>
          onDetailsChange({ Field: val, Value: undefined, Values: [] })
        }
        allQuestionIds={allQuestionIds}
      />
      <div>
        <label className="text-xs font-medium text-gray-600">
          Has Value(s)...
        </label>
        {isDynamic && lookupOptions.length > 0 ? (
          <div className="mt-1 p-2 border rounded-md bg-white max-h-32 overflow-y-auto">
            {lookupOptions.map((opt, index) => (
              <label key={`${opt.value}-${index}`} className="flex items-center text-sm py-1">
                <input
                  type="checkbox"
                  checked={currentValues.includes(opt.value)}
                  onChange={() => handleMultiSelectChange(opt.value)}
                  className="mr-2 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                {opt.label}
              </label>
            ))}
          </div>
        ) : isSelectableType && selectedQuestion.options ? (
          <div className="mt-1 p-2 border rounded-md bg-white max-h-32 overflow-y-auto">
            {selectedQuestion.options.map((opt, index) => {
              const optionValue = typeof opt === "string" ? opt : opt.value;
              const optionLabel = typeof opt === "string" ? opt : opt.label;
              return (
                <label
                  key={`${optionValue}-${index}`}
                  className="flex items-center text-sm py-1"
                >
                  <input
                    type="checkbox"
                    checked={currentValues.includes(optionValue)}
                    onChange={() => handleMultiSelectChange(optionValue)}
                    className="mr-2 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  {optionLabel}
                </label>
              );
            })}
          </div>
        ) : (
          <>
            <input
              type="text"
              value={currentValues.join(", ")}
              onChange={handleTextValuesChange}
              className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
              placeholder="Enter comma-separated values"
            />
            <span className="italic text-gray-400 text-xs block mt-1">
              For multiple values, separate them with a comma.
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const TextFieldWithDefaultForm: React.FC<{
  details: Partial<TextFieldDefault>;
  onDetailsChange: (newDetails: Partial<TextFieldDefault>) => void;
  allQuestionIds: string[];
}> = ({ details, onDetailsChange, allQuestionIds }) => (
  <div className="space-y-2">
    <QuestionIdSelect
      label="For Question ID..."
      value={details.Field || ""}
      onChange={(val) => onDetailsChange({ Field: val })}
      allQuestionIds={allQuestionIds}
    />
    <div>
      <label className="text-xs font-medium text-gray-600">Default Value</label>
      <input
        type="text"
        value={details.Default || ""}
        onChange={(e) => onDetailsChange({ Default: e.target.value })}
        className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
        placeholder="Enter default text"
      />
    </div>
  </div>
);

const PairedCheckboxForm: React.FC<{
  details: { dataKey: string; role: "Yes" | "No"; pair: CheckBoxMapping };
  pdfFieldName: string;
  onDetailsChange: (newDetails: {
    dataKey: string;
    pair: CheckBoxMapping;
    oldDataKey: string;
  }) => void;
  allQuestionIds: string[];
  allPdfCheckboxFields: DiscoveredPdfField[];
}> = ({
  details,
  pdfFieldName,
  onDetailsChange,
  allQuestionIds,
  allPdfCheckboxFields,
}) => {
    const { dataKey, role, pair } = details;
    const partnerFieldName = role === "Yes" ? pair.No : pair.Yes;

    const handleUpdate = (updates: {
      dataKey?: string;
      role?: "Yes" | "No";
      partnerField?: string;
    }) => {
      const newDataKey = updates.dataKey ?? dataKey;
      const newRole = updates.role ?? role;
      const newPartnerField = updates.partnerField ?? partnerFieldName;

      const newPair: CheckBoxMapping = {
        Yes: newRole === "Yes" ? pdfFieldName : newPartnerField,
        No: newRole === "No" ? pdfFieldName : newPartnerField,
      };

      onDetailsChange({
        dataKey: newDataKey,
        pair: newPair,
        oldDataKey: dataKey,
      });
    };

    return (
      <div className="space-y-2">
        <QuestionIdSelect
          label="Controlled by Question ID..."
          value={dataKey}
          onChange={(val) => handleUpdate({ dataKey: val })}
          allQuestionIds={allQuestionIds}
        />
        <div>
          <label className="text-xs font-medium text-gray-600">
            This PDF Field is the...
          </label>
          <div className="flex items-center space-x-4 mt-1">
            <label className="flex items-center text-sm">
              <input
                type="radio"
                value="Yes"
                checked={role === "Yes"}
                onChange={() => handleUpdate({ role: "Yes" })}
                className="mr-1"
              />{" "}
              Yes Box
            </label>
            <label className="flex items-center text-sm">
              <input
                type="radio"
                value="No"
                checked={role === "No"}
                onChange={() => handleUpdate({ role: "No" })}
                className="mr-1"
              />{" "}
              No Box
            </label>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">
            Partner Field (The Other Box)
          </label>
          <select
            value={partnerFieldName || ""}
            onChange={(e) => handleUpdate({ partnerField: e.target.value })}
            className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
          >
            <option value="" disabled>
              -- Select Partner Field --
            </option>
            {/* Exclude the current field from the list of possible partners */}
            {allPdfCheckboxFields
              .filter((f) => f.name !== pdfFieldName)
              .map((f) => (
                <option key={f.name} value={f.name}>
                  {f.name}
                </option>
              ))}
          </select>
        </div>
      </div>
    );
  };

const AggregatedTextFieldForm: React.FC<{
  details: Partial<AggregatedTextField>;
  onDetailsChange: (newDetails: Partial<AggregatedTextField>) => void;
}> = ({ details, onDetailsChange }) => (
  <div className="space-y-2">
    <div>
      <label className="text-xs font-medium text-gray-600">Answer Prefix</label>
      <span className="italic text-gray-400 text-xs block">This is going to be the starting name of all text-field answers. For example "comment_" might denote every comment field to be aggregated in a single details section.</span>
      <input
        type="text"
        value={details.Prefix || ""}
        onChange={(e) => onDetailsChange({ Prefix: e.target.value })}
        className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
        placeholder="e.g., comment_"
      />
    </div>
    <div>
      <label className="text-xs font-medium text-gray-600">Separator</label>
      <input
        type="text"
        value={details.Separator || ""}
        onChange={(e) => onDetailsChange({ Separator: e.target.value })}
        className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
        placeholder="e.g., \n\n for new lines"
      />
    </div>
  </div>
);

const ConditionalTextFieldForm: React.FC<{
  details: Partial<ConditionalTextField>;
  onDetailsChange: (newDetails: Partial<ConditionalTextField>) => void;
  allQuestionIds: string[];
}> = ({ details, onDetailsChange, allQuestionIds }) => {

  const handleSuffixChange = (index: number, newSuffix: Partial<ConditionalValueSuffix>) => {
    const updatedSuffixes = [...(details.ValueSuffixes || [])];
    updatedSuffixes[index] = newSuffix as ConditionalValueSuffix;
    onDetailsChange({ ValueSuffixes: updatedSuffixes });
  };

  const addSuffix = () => {
    const newSuffix: ConditionalValueSuffix = { SourcePrefix: '', CombineResult: false, Condition: {} };
    const updatedSuffixes = [...(details.ValueSuffixes || []), newSuffix];
    onDetailsChange({ ValueSuffixes: updatedSuffixes });
  };

  const deleteSuffix = (index: number) => {
    const updatedSuffixes = (details.ValueSuffixes || []).filter((_, i) => i !== index);
    onDetailsChange({ ValueSuffixes: updatedSuffixes });
  };

  return (
    <div className="space-y-3">
      <QuestionIdSelect label="Source Question ID" value={details.SourceField || ''} onChange={(val) => onDetailsChange({ SourceField: val })} allQuestionIds={allQuestionIds} />
      <div>
        <label className="text-xs font-medium text-gray-600">Value Suffix Rules</label>
        <span className="italic text-gray-400 text-xs block">Define a list of rules that find and format additional text based on each answer from the "Source Question".</span>
        <div className="mt-1 p-2 border-t border-b border-gray-200 space-y-2">
          {(details.ValueSuffixes || []).map((suffix, index) => (
            <ValueSuffixEditor key={index} suffix={suffix} onSuffixChange={(s) => handleSuffixChange(index, s)} onDelete={() => deleteSuffix(index)} />
          ))}
        </div>
        <button onClick={addSuffix} className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 mt-2 rounded">+ Add Suffix Rule</button>
      </div>
    </div>
  );
};

const ObjectPropertyTextFieldForm: React.FC<{
  details: { objectName: string; sourceField: string, combineWith?: string, separator?: string };
  onDetailsChange: (newDetails: { objectName: string; sourceField: string, combineWith?: string, separator?: string }) => void;
  formDefinition: FormDefinition;
}> = ({ details, onDetailsChange, formDefinition }) => {
  const dynamicSourceQuestions = formDefinition.steps
    .flatMap(step => step.questions)
    .filter(q => !!q.dynamicSource);

  const selectedQuestion = dynamicSourceQuestions.find(q => q.id === details.objectName);

  const DYNAMIC_SOURCE_SHAPES: Record<string, string[]> = {
    'participants': ['id', 'firstName', 'lastName', 'fullName', 'caseNumber', 'dateOfBirth', 'email', 'phoneNumber', 'waiver.waiverName']
  };

  const sourceFieldOptions = selectedQuestion?.dynamicSource
    ? DYNAMIC_SOURCE_SHAPES[selectedQuestion.dynamicSource] || []
    : [];

  const handleSourceFieldChange = (value: string) => {
    if (value === 'fullName') {
      onDetailsChange({
        ...details,
        sourceField: 'firstName',
        combineWith: 'lastName',
        separator: ' '
      });
    } else {
      onDetailsChange({
        ...details,
        sourceField: value,
        combineWith: undefined,
        separator: undefined
      });
    }
  };

  const displayedValue = details.sourceField === 'firstName' && details.combineWith === 'lastName'
    ? 'fullName'
    : details.sourceField || '';

  return (
    <div className="space-y-2">
      <div>
        <label className="text-xs font-medium text-gray-600">Source Question (Object)</label>
        <select
          value={details.objectName || ''}
          onChange={e => onDetailsChange({ objectName: e.target.value, sourceField: '' })}
          className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
        >
          <option value="" disabled>-- Select Object Question --</option>
          {dynamicSourceQuestions.map(q => (
            <option key={q.id} value={q.id}>{q.id} ({q.dynamicSource})</option>
          ))}
        </select>
      </div>
      {details.objectName && (
        <>
          <div>
            <label className="text-xs font-medium text-gray-600">Property to Extract</label>
            <select
              value={displayedValue}
              onChange={e => handleSourceFieldChange(e.target.value)}
              className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
            >
              <option value="" disabled>-- Select Property --</option>
              {sourceFieldOptions.map(prop => (
                <option key={prop} value={prop}>{prop}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Combine With (Optional)</label>
            <select
              value={details.combineWith || ''}
              onChange={e => onDetailsChange({ ...details, combineWith: e.target.value || undefined })}
              className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
            >
              <option value="">-- None --</option>
              {sourceFieldOptions.map(prop => (
                <option key={prop} value={prop}>{prop}</option>
              ))}
            </select>
          </div>
          {details.combineWith && (
            <div>
              <label className="text-xs font-medium text-gray-600">Separator</label>
              <input
                type="text"
                value={details.separator || ''}
                onChange={e => onDetailsChange({ ...details, separator: e.target.value })}
                className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
                placeholder="e.g., a space ' '"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const LookupTextFieldForm: React.FC<{
  details: Partial<LookupTextField>;
  onDetailsChange: (newDetails: Partial<LookupTextField>) => void;
  allQuestionIds: string[];
  formDefinition: FormDefinition;
}> = ({ details, onDetailsChange, allQuestionIds, formDefinition }) => {
  const { currentUser } = useAuth();
  const [lookupOptions, setLookupOptions] = useState<{ value: string; label: string }[]>([]);

  const selectedQuestion = details.SourceField
    ? formDefinition.steps
      .flatMap((step) => step.questions)
      .find((q) => q.id === details.SourceField)
    : null;

  useEffect(() => {
    const fetchLookupOptions = async () => {
      if (!selectedQuestion?.dynamicSource || !currentUser) {
        setLookupOptions([]);
        return;
      }

      const token = await currentUser.getIdToken();
      let options: { value: string; label: string }[] = [];

      if (selectedQuestion.dynamicSource === 'taskContactTypes') {
        const taskTypes = await getTaskTypes(token);
        if (taskTypes) {
          options = taskTypes.map(t => ({ value: t.type, label: t.type }));
        }
      } else if (selectedQuestion.dynamicSource === 'waiverTypes') {
        const waiverTypes = await getWaiverTypes(token);
        if (waiverTypes) {
          options = waiverTypes.map(w => ({ value: w.name, label: w.name }));
        }
      }

      setLookupOptions(options);
    };

    fetchLookupOptions();
  }, [selectedQuestion, currentUser]);

  return (
    <div className="space-y-2">
      <QuestionIdSelect
        label="Source Question"
        value={details.SourceField || ""}
        onChange={(val) => onDetailsChange({ SourceField: val, Value: '' })}
        allQuestionIds={allQuestionIds.filter(id =>
          formDefinition.steps.flatMap(s => s.questions).find(q => q.id === id)?.dynamicSource
        )}
      />
      {lookupOptions.length > 0 && (
        <div>
          <label className="text-xs font-medium text-gray-600">If Value Is</label>
          <select
            value={details.Value || ""}
            onChange={(e) => onDetailsChange({ Value: e.target.value, Text: e.target.value })}
            className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
          >
            <option value="" disabled>-- Select Value --</option>
            {lookupOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="text-xs font-medium text-gray-600">Set PDF Field to Text</label>
        <input
          type="text"
          value={details.Text || ""}
          onChange={(e) => onDetailsChange({ Text: e.target.value })}
          className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
          placeholder="Text to display in PDF"
        />
      </div>
    </div>
  );
};


const AuthorInformationForm: React.FC<{
  details: { authorField: keyof PdfMappingDefinition['AuthorInfoFields'] };
  onDetailsChange: (newDetails: { authorField: keyof PdfMappingDefinition['AuthorInfoFields'] }) => void;
}> = ({ details, onDetailsChange }) => {
  const AUTHOR_FIELD_OPTIONS: Array<keyof AuthorInfoFields> = [
    "SCID",
    "SCFullName",
    "SupervisorFullName",
    "ProgramManagerFullName",
    "TaskType",
    "OrganizationName"
  ];

  return (
    <div className="space-y-2">
      <div>
        <label className="text-xs font-medium text-gray-600">Author Field</label>
        <select
          value={details.authorField || ""}
          onChange={e => onDetailsChange({ authorField: e.target.value as keyof PdfMappingDefinition['AuthorInfoFields'] })}
          className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
        >
          <option value="" disabled>-- Select Author Field --</option>
          {AUTHOR_FIELD_OPTIONS.map(field => (
            <option key={field} value={field}>{field}</option>
          ))}
        </select>
      </div>
    </div>
  );
};


// --- Main Editor & Row Components ---

interface MappingEditorProps {
  discoveredPdfFields: DiscoveredPdfField[];
  allQuestionIds: string[];
  currentMapping: PdfMappingDefinition;
  onMappingChange: (newMapping: PdfMappingDefinition) => void;
  formDefinition: FormDefinition;
}

const MappingEditor: React.FC<MappingEditorProps> = (props) => {
  const pdfCheckboxFields = props.discoveredPdfFields.filter(
    (f) => f.type === "checkbox"
  );

  if (props.discoveredPdfFields.length === 0) {
    return (
      <p className="text-gray-500 italic p-4">
        Upload a PDF to see its fillable fields here.
      </p>
    );
  }

  return (
    <div className="p-4">
      <p className="text-sm text-gray-600 mb-4">
        For each PDF field, select how it should be mapped and configure its
        behavior.
      </p>
      {props.discoveredPdfFields.map((field, index) => (
        <FieldMappingRow
          key={`${field.name}-${index}`}
          {...props}
          field={field}
          pdfCheckboxFields={pdfCheckboxFields}
        />
      ))}
    </div>
  );
};

const FieldMappingRow: React.FC<
  MappingEditorProps & {
    field: DiscoveredPdfField;
    pdfCheckboxFields: DiscoveredPdfField[];
  }
> = ({
  field,
  allQuestionIds,
  currentMapping,
  onMappingChange,
  formDefinition,
  pdfCheckboxFields,
}) => {
    const mappingInfo = findMappingForPdfField(field.name, currentMapping);

    const handleUnmap = () => {
      const newMapping = removeMappingForPdfField(field.name, currentMapping);
      onMappingChange(newMapping);
    };

    const handleMappingTypeChange = (newType: UIMappingType | "") => {
      if (!newType) return handleUnmap();

      // First, remove any old mapping for this field to ensure a clean slate
      let newMapping = removeMappingForPdfField(field.name, currentMapping);

      // Then, add a new default entry for the selected type
      if (newType === "Conditional Checkbox") {
        const newCondition: CheckBoxCondition = {
          Field: "",
          Values: [],
          Checkbox: field.name,
        };
        newMapping.CheckBoxConditions = [
          ...(newMapping.CheckBoxConditions || []),
          newCondition,
        ];
      } else if (newType === "Direct Text Field") {
        const newDataKey = `question_for_${field.name.replace(/\s/g, "_")}`;
        newMapping.TextFieldMappings = {
          ...(newMapping.TextFieldMappings || {}),
          [newDataKey]: field.name,
        };
      } else if (newType === "Text Field with Default") {
        const newDefault: TextFieldDefault = {
          Field: "",
          PdfField: field.name,
          Default: "",
        };
        newMapping.TextFieldDefaults = [
          ...(newMapping.TextFieldDefaults || []),
          newDefault,
        ];
      } else if (newType === "Paired Yes/No Checkbox") {
        const newDataKey = `question_for_${field.name.replace(/\s/g, "_")}`;
        const newPair: CheckBoxMapping = { Yes: field.name, No: "" }; // User needs to select the 'No' field
        newMapping.CheckBoxMappings = {
          ...(newMapping.CheckBoxMappings || {}),
          [newDataKey]: newPair,
        };
      } else if (newType === "Aggregated Text Field") {
        const newAgg: AggregatedTextField = {
          Prefix: "",
          PdfField: field.name,
          Separator: "\\n\\n",
        };
        newMapping.AggregatedTextFields = [
          ...(newMapping.AggregatedTextFields || []),
          newAgg,
        ];
      } else if (newType === "Conditional Text Field") {
        const newCond: ConditionalTextField = {
          ResultField: field.name,
          SourceField: "",
          ValueSuffixes: [],
        };
        newMapping.ConditionalTextFields = [
          ...(newMapping.ConditionalTextFields || []),
          newCond,
        ];
      } else if (newType === "Author Information") {
        // Handle potential mixed types
        let newAuthorInfo: any[] = [];

        if (newMapping.AuthorInfoFields) {
          if (Array.isArray(newMapping.AuthorInfoFields)) {
            newAuthorInfo = [...newMapping.AuthorInfoFields];
          } else {
            // MIGRATE ON THE FLY: Convert old object to array
            const oldObj = newMapping.AuthorInfoFields as any;
            Object.keys(oldObj).forEach(key => {
              newAuthorInfo.push({ AuthorField: key, PdfField: oldObj[key] });
            });
          }
        }

        // Push the new mapping
        newAuthorInfo.push({
          AuthorField: "SCID",
          PdfField: field.name
        });

        newMapping.AuthorInfoFields = newAuthorInfo;
      } else if (newType === "Object Property Text Field") {
        const newObjMapping: ObjectMapping = {
          ObjectName: '', // User needs to select this
          Fields: [{ SourceField: '', PdfField: field.name }]
        };
        newMapping.ObjectMappings = [...(newMapping.ObjectMappings || []), newObjMapping];
      } else if (newType === "Lookup Value to Text Field") {
        const newLookup: any = {
          SourceField: "",
          Value: "",
          Text: "",
          PdfField: field.name,
        };
        (newMapping as any).LookupTextFields = [
          ...((newMapping as any).LookupTextFields || []),
          newLookup,
        ];
      }


      onMappingChange(newMapping);
    };

    const handleDetailsChange = (newDetails: any) => {
      const newMapping = { ...currentMapping };

      if (mappingInfo.type === "Conditional Checkbox") {
        const index = (newMapping.CheckBoxConditions || []).findIndex(
          (c) => c.Checkbox === field.name
        );
        if (index > -1) {
          const updatedConditions = [...newMapping.CheckBoxConditions!];
          updatedConditions[index] = {
            ...updatedConditions[index],
            ...newDetails,
          };
          newMapping.CheckBoxConditions = updatedConditions;
          onMappingChange(newMapping);
        }
      } else if (mappingInfo.type === "Text Field with Default") {
        const index = (newMapping.TextFieldDefaults || []).findIndex(
          (d) => d.PdfField === field.name
        );
        if (index > -1) {
          const updatedDefaults = [...newMapping.TextFieldDefaults!];
          updatedDefaults[index] = { ...updatedDefaults[index], ...newDetails };
          newMapping.TextFieldDefaults = updatedDefaults;
          onMappingChange(newMapping);
        }
      } else if (mappingInfo.type === "Paired Yes/No Checkbox") {
        const { dataKey, pair, oldDataKey } = newDetails;
        const updatedMaps = { ...(newMapping.CheckBoxMappings || {}) };
        // If the dataKey (questionId) was changed, remove the old entry
        if (oldDataKey && oldDataKey !== dataKey) {
          delete updatedMaps[oldDataKey];
        }
        updatedMaps[dataKey] = pair;
        newMapping.CheckBoxMappings = updatedMaps;
        onMappingChange(newMapping);
      } else if (mappingInfo.type === "Aggregated Text Field") {
        const index = (newMapping.AggregatedTextFields || []).findIndex(
          (a) => a.PdfField === field.name
        );
        if (index > -1) {
          const updatedAggs = [...newMapping.AggregatedTextFields!];
          updatedAggs[index] = { ...updatedAggs[index], ...newDetails };
          newMapping.AggregatedTextFields = updatedAggs;
          onMappingChange(newMapping);
        }
      } else if (mappingInfo.type === "Conditional Text Field") {
        const index = (newMapping.ConditionalTextFields || []).findIndex(
          (c) => c.ResultField === field.name
        );
        if (index > -1) {
          const updatedConds = [...newMapping.ConditionalTextFields!];
          updatedConds[index] = { ...updatedConds[index], ...newDetails };
          newMapping.ConditionalTextFields = updatedConds;
          onMappingChange(newMapping);
        }
      } else if (mappingInfo.type === "Author Information") {
        const { authorField } = newDetails;

        const newAuthorInfo = [...(newMapping.AuthorInfoFields || [])];
        const index = newAuthorInfo.findIndex(m => m.PdfField === field.name);

        if (index > -1) {
          // Update existing mapping
          newAuthorInfo[index] = { ...newAuthorInfo[index], AuthorField: authorField };
          newMapping.AuthorInfoFields = newAuthorInfo;
          onMappingChange(newMapping);
        }
      } else if (mappingInfo.type === "Object Property Text Field") {
        const { objectName, sourceField, combineWith, separator } = newDetails;
        const objMappingIndex = (newMapping.ObjectMappings || []).findIndex(om =>
          om.Fields.some(f => f.PdfField === field.name)
        );

        if (objMappingIndex > -1) {
          const newObjectMappings = [...newMapping.ObjectMappings!];
          const targetObjectMapping = { ...newObjectMappings[objMappingIndex] };
          const fieldIndex = targetObjectMapping.Fields.findIndex(f => f.PdfField === field.name);

          if (fieldIndex > -1) {
            // Update the source field within the found field mapping
            const newFields = [...targetObjectMapping.Fields];
            const currentField = { ...newFields[fieldIndex] };

            (currentField as any).SourceField = sourceField;
            (currentField as any).CombineWith = combineWith;
            (currentField as any).Separator = separator;
            newFields[fieldIndex] = currentField;

            // Update the object name for the entire mapping
            targetObjectMapping.ObjectName = objectName;
            targetObjectMapping.Fields = newFields;

            newObjectMappings[objMappingIndex] = targetObjectMapping;
            newMapping.ObjectMappings = newObjectMappings;
          }
        }
        onMappingChange(newMapping);
      } else if (mappingInfo.type === "Lookup Value to Text Field") {
        const index = ((newMapping as any).LookupTextFields || []).findIndex(
          (l: any) => l.PdfField === field.name
        );
        if (index > -1) {
          const updatedLookups = [...(newMapping as any).LookupTextFields];
          updatedLookups[index] = { ...updatedLookups[index], ...newDetails, PdfField: field.name };
          (newMapping as any).LookupTextFields = updatedLookups;
          onMappingChange(newMapping);
        }
      }
    };

    return (
      <div className="border border-gray-200 p-3 rounded-lg mb-3 bg-gray-50">
        <div className="flex justify-between items-start">
          <strong className="font-medium text-gray-700">{field.name}</strong>
          {mappingInfo.isMapped && (
            <button
              onClick={handleUnmap}
              className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium hover:bg-red-200"
            >
              Unmap
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <label className="text-xs font-medium text-gray-600">
              Mapping Type
            </label>
            <select
              value={mappingInfo.type || ""}
              onChange={(e) =>
                handleMappingTypeChange(e.target.value as UIMappingType | "")
              }
              className="block w-full mt-1 px-2 py-1 border rounded-md text-sm"
            >
              <option value="">-- Unmapped --</option>
              {MAPPING_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="border-l pl-4">
            <h4 className="text-xs font-medium text-gray-600 mb-1">
              Configuration
            </h4>
            {!mappingInfo.isMapped && (
              <p className="text-xs text-gray-500 italic mt-1">
                Select a mapping type to begin.
              </p>
            )}

            {mappingInfo.type === "Conditional Checkbox" && (
              <ConditionalCheckboxForm
                details={mappingInfo.details}
                onDetailsChange={handleDetailsChange}
                allQuestionIds={allQuestionIds}
                formDefinition={formDefinition}
              />
            )}
            {mappingInfo.type === "Direct Text Field" && (
              <QuestionIdSelect
                label="Is Filled by Data Key..."
                value={mappingInfo.details.dataKey}
                onChange={(newQuestionId) => {
                  const oldKey = mappingInfo.details.dataKey;
                  const updatedTextFields = {
                    ...currentMapping.TextFieldMappings,
                  };
                  delete updatedTextFields[oldKey];
                  updatedTextFields[newQuestionId] = field.name;
                  onMappingChange({
                    ...currentMapping,
                    TextFieldMappings: updatedTextFields,
                  });
                }}
                allQuestionIds={allQuestionIds}
              />
            )}
            {mappingInfo.type === "Text Field with Default" && (
              <TextFieldWithDefaultForm
                details={mappingInfo.details}
                onDetailsChange={handleDetailsChange}
                allQuestionIds={allQuestionIds}
              />
            )}
            {mappingInfo.type === "Paired Yes/No Checkbox" && (
              <PairedCheckboxForm
                details={mappingInfo.details}
                pdfFieldName={field.name}
                onDetailsChange={handleDetailsChange}
                allQuestionIds={allQuestionIds}
                allPdfCheckboxFields={pdfCheckboxFields}
              />
            )}
            {mappingInfo.type === "Aggregated Text Field" && (
              <AggregatedTextFieldForm
                details={mappingInfo.details}
                onDetailsChange={handleDetailsChange}
              />
            )}
            {mappingInfo.type === "Conditional Text Field" && (
              <ConditionalTextFieldForm
                details={mappingInfo.details}
                onDetailsChange={handleDetailsChange}
                allQuestionIds={allQuestionIds}
              />
            )}
            {mappingInfo.type === "Author Information" && (
              <AuthorInformationForm
                details={mappingInfo.details}
                onDetailsChange={handleDetailsChange}
              />
            )}
            {mappingInfo.type === "Object Property Text Field" && (
              <ObjectPropertyTextFieldForm
                details={mappingInfo.details}
                onDetailsChange={handleDetailsChange}
                formDefinition={formDefinition}
              />
            )}
            {mappingInfo.type === "Lookup Value to Text Field" && (
              <LookupTextFieldForm
                details={mappingInfo.details}
                onDetailsChange={handleDetailsChange}
                allQuestionIds={allQuestionIds}
                formDefinition={formDefinition}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

export default MappingEditor;
